import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';

import { GlobalStyles } from './styles/GlobalStyles';
import Header from './components /Header';
import RouteSeo from './components /RouteSeo';
import HomePage from './components /Homepage';
import CookieBanner from './components /Cookiebanner';
import { Providers } from './providers';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import '@ionic/react/css/core.css';
import { setupIonicReact } from '@ionic/react';

setupIonicReact();

const TradesmanSignup = lazy(() => import('./components /TradesmanSignup'));
const Login = lazy(() => import('./components /Login'));
const TradesmanHomepage = lazy(() => import('./components /TradesmanHomePage'));
const PostJobPage = lazy(() => import('./components /Raising Jobs/PostJobPage'));
const LoggedInPostJobPage = lazy(() => import('./components /Raising Jobs/LoggedInPostJobPage'));
const ViewInterestPage = lazy(() => import('./components /ViewInterestPage'));
const PaymentPage = lazy(() => import('./components /PaymentPage'));
const ClientDetailsPage = lazy(() => import('./components /ClientDetailsPage'));
const CustomerReviews = lazy(() => import('./components /CustomerReviews'));
const TermsAndConditions = lazy(() => import('./components /TermsAndConditions'));
const CustomerHomepage = lazy(() => import('./components /CustomerHomePage'));
const SubPage = lazy(() => import('./components /SubPage'));
const ManageSubscription = lazy(() => import('./components /ManageSubscription'));
const HowItWorksBlog = lazy(() => import('./components /HowItWorksBlog'));
const ReviewForm = lazy(() => import('./components /ReviewForm'));
const ChatRoom = lazy(() => import('./components /ChatRoom'));
const JobDetails = lazy(() => import('./components /JobDetails'));
const SubscriptionSuccess = lazy(() => import('./components /SubcriptionSuccess'));
const FindTradesman = lazy(() => import('./components /findTrade'));
const TradesDetail = lazy(() => import('./components /TradeDetail'));
const ComingSoonPage = lazy(() => import('./components /ComingSoon'));
const Privacy = lazy(() => import('./components /Privacypolicy'));
const Cookie = lazy(() => import('./components /cookiepolicy'));
const TradespersonSignupInfoPage = lazy(() => import('./components /TradespersonSignupInfoPage'));
const ResetPassword = lazy(() => import('./components /ResetPassword'));
const ResetPasswordRequest = lazy(() => import('./components /ResetPasswordRequest'));
const Dashboard = lazy(() => import('./components /dashboard'));
const TradesmanQuickSignUp = lazy(() => import('./components /TradesmansQuickSignUp'));
const LocationServicePage = lazy(() => import('./components /LocationServicePage'));
const JoinServicePage = lazy(() => import('./components /JoinServicePage'));
const MinorWorksCertApp = lazy(() => import('./components /MinorWorks'));
const ResourcesDashboard = lazy(() => import('./components /ResourcesDashboard'));
const EICCert = lazy(() => import('./components /EICCertApp'));
const EICRCertApp = lazy(() => import('./components /EICRCertApp'));
const SiteAuditResource = lazy(() => import('./components /SiteAuditResource'));
const QuoteInvoiceResource = lazy(() => import('./components /QuoteInvoiceResource'));
const SavedCertificates = lazy(() => import('./components /SavedCertificates'));
const TradesmanHomepagePreview = lazy(() => import('./components /TradesmanHomepagePreview'));

const MainContainer = styled.div`
  margin-top: 0;
  padding: 0;
`;

const RouteLoading = styled.div`
  min-height: 30vh;
`;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState(null);
  const [userType, setUserType] = useState('');
  const [formState, setFormState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profilePicture] = useState({
    profilePicture: { data: { $binary: { base64: '' } }, contentType: '' },
  });

  useEffect(() => {
    const storedAuth = localStorage.getItem('isAuthenticated');
    const storedUserType = localStorage.getItem('userType');
    const storedUserId = localStorage.getItem('userId');

    if (storedAuth === 'true' && storedUserId && storedUserType) {
      setIsAuthenticated(true);
      setUserType(storedUserType);
      setUserId(storedUserId);
    } else {
      setIsAuthenticated(false);
      setUserType('');
      setUserId(null);
    }

    setIsLoading(false);
  }, []);

  const handleLogin = (id, type) => {
    setIsAuthenticated(true);
    setUserId(id);
    setUserType(type);

    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('userType', type);
    localStorage.setItem('userId', id);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUserId(null);
    setUserType('');
    setFormState(null);

    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userType');
    localStorage.removeItem('userId');
    localStorage.removeItem('tradesmanId');
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Router>
      <Providers>
        <GlobalStyles />
        <RouteSeo />
        <Header
          isAuthenticated={isAuthenticated}
          handleLogout={handleLogout}
          userType={userType}
          profilePicture={profilePicture}
        />

        <MainContainer>
          <Suspense fallback={<RouteLoading />}>
            <Routes>
              <Route path="/" element={<HomePage formState={formState} setFormState={setFormState} />} />
              <Route path="/join/:slug" element={<JoinServicePage />} />
              <Route
                path="/post-job"
                element={
                  isAuthenticated ? (
                    <LoggedInPostJobPage customerId={userId} />
                  ) : (
                    <PostJobPage formState={formState} setFormState={setFormState} />
                  )
                }
              />
              <Route path="/resources" element={<ResourcesDashboard />} />
              <Route path="/site-audit-report" element={<SiteAuditResource />} />
              <Route path="/quote-invoice-tool" element={<QuoteInvoiceResource />} />
              <Route path="/saved-certs" element={<SavedCertificates />} />
              <Route path="/eic-cert" element={<EICCert />} />
              <Route path="/eicr-cert" element={<EICRCertApp />} />
              <Route path="/reset-password" element={<ResetPasswordRequest />} />
              <Route path="/fast-signup" element={<TradesmanQuickSignUp />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/tradesman/:id" element={<TradesDetail />} />
              <Route path="/job-details/:jobId" element={<JobDetails />} />
              <Route path="/tradesignup" element={<TradespersonSignupInfoPage />} />
              <Route path="/comingsoon" element={<ComingSoonPage />} />
              <Route path="/how-it-works" element={<HowItWorksBlog />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/cookie" element={<Cookie />} />
              <Route path="/cert-minor" element={<MinorWorksCertApp />} />
              <Route path="/find-tradesman" element={<FindTradesman formState={formState} setFormState={setFormState} />} />
              <Route path="/signup" element={<TradesmanSignup />} />
              <Route path="/login" element={<Login onLogin={handleLogin} />} />
              <Route
                path="/tradesman-homepage"
                element={isAuthenticated && userType === 'tradesman' ? <TradesmanHomepage tradesmanId={userId} /> : <Navigate to="/login" />}
              />
              <Route
                path="/customer-homepage"
                element={isAuthenticated && userType === 'customer' ? <CustomerHomepage customerId={userId} /> : <Navigate to="/login" />}
              />
              <Route path="/view-interest/:jobId" element={<ViewInterestPage />} />
              <Route path="/payment" element={<PaymentPage />} />
              <Route path="/clientdetailspage/" element={<ClientDetailsPage />} />
              <Route path="/reviews/:tradesmanId" element={<CustomerReviews />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions formState={formState} />} />
              <Route path="/review/:jobId/:tradesmanId" element={<ReviewForm />} />
              <Route path="/subscribe" element={<SubPage />} />
              <Route path="/stripe/subscription-success" element={<SubscriptionSuccess />} />
              <Route
                path="/manage-subscription"
                element={isAuthenticated && userType === 'tradesman' ? <ManageSubscription tradesmanId={userId} /> : <Navigate to="/login" />}
              />
              <Route path="/chatroom/:jobId" element={<ChatRoom />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/services/:slug" element={<LocationServicePage />} />
              <Route path="/tradesman-homepage-preview" element={<TradesmanHomepagePreview />} />
            </Routes>
          </Suspense>
          <CookieBanner />
        </MainContainer>
      </Providers>
    </Router>
  );
}

export default App;
